import { usePlayer } from "../player/playerContext";
import { useQuery, useQueryCache } from "react-query";
import fetchApi from "../backend/fetchApi";

export interface OutcomeTreeData {
    [gameNumber: number]: { "1": number; "X": number; "2": number };
}

export interface TotalRowsPerGame {
    [gameNumber: number]: number;
}

interface TreeNode {
    fixtureNo: number;
    sign: string;
    path: string;
    stake: number;
    one?: TreeNode;
    x?: TreeNode;
    two?: TreeNode;
}

interface ApiResponse {
    roundId: string;
    currency: string;
    tree: TreeNode;
}

const navigateToSign = (node: TreeNode, sign: string): TreeNode | undefined => {
    if (sign === "1") return node.one;
    if (sign === "X" || sign === "x") return node.x;
    if (sign === "2") return node.two;
    return undefined;
};

const sumStake = (nodes: (TreeNode | undefined)[]) =>
    nodes.reduce((sum, node) => sum + (node?.stake ?? 0), 0);

const useOutcomeTree = (roundId: string | number | undefined, correctSigns: (string | undefined)[], stakePerRow: number = 1) => {
    const [player] = usePlayer() as any;
    const queryCache = useQueryCache();

    const roundsUrl = player && player._links.rounds.href;
    // correctSigns is index aligned with the games, undefined where a game has not finished yet
    const pattern = correctSigns.slice();
    while (pattern.length > 0 && !pattern[pattern.length - 1]) {
        pattern.pop();
    }
    const rowPattern = pattern.map((sign) => sign ?? '?').join('');
    const enabled = !!roundsUrl && !!roundId;
    const queryKey = ['outcomeTree', roundId, rowPattern, stakePerRow];

    const { status, error, data } = useQuery(
        queryKey,
        async () => {
            if (!roundsUrl || !roundId) return null;

            // The rowPattern prunes every branch that does not follow the correct signs, but the tree is still
            // rooted at game 1 and each game keeps its full 1/X/2 distribution. Only games without a correct
            // sign branch out, so they are the only ones that cost a level, plus the first game after the
            // pattern and its children.
            const levels = pattern.filter((sign) => !sign).length + 2;
            const params = new URLSearchParams({ levels: String(levels) });
            if (rowPattern) {
                params.set('rowPattern', rowPattern);
            }
            const url = `${roundsUrl}/${roundId}/outcomeTree?${params.toString()}`;

            const json: ApiResponse | null = await fetchApi(url)
                .then((r: Response) => r.json())
                .catch(() => null);

            if (!json?.tree) return null;

            const outcomeData: OutcomeTreeData = {};
            const totalRows: TotalRowsPerGame = {};
            const divisor = stakePerRow > 0 ? stakePerRow : 1;

            // Walk the tree one game at a time. A game that has not finished yet keeps all three branches, so
            // the walk carries a frontier of nodes rather than a single node.
            let frontier: TreeNode[] = [json.tree];
            for (let gameIdx = 0; frontier.length > 0; gameIdx++) {
                if (!frontier.some((node) => node.one || node.x || node.two)) break;

                const gameNumber = gameIdx + 1;
                outcomeData[gameNumber] = {
                    "1": Math.round(sumStake(frontier.map((node) => node.one)) / divisor),
                    "X": Math.round(sumStake(frontier.map((node) => node.x)) / divisor),
                    "2": Math.round(sumStake(frontier.map((node) => node.two)) / divisor),
                };
                totalRows[gameNumber] = Math.round(sumStake(frontier) / divisor);

                if (gameIdx >= pattern.length) break;

                const correctSign = pattern[gameIdx];
                const nextFrontier: TreeNode[] = [];
                frontier.forEach((node) => {
                    const children = correctSign
                        ? [navigateToSign(node, correctSign)]
                        : [node.one, node.x, node.two];
                    children.forEach((child) => {
                        if (child) nextFrontier.push(child);
                    });
                });
                frontier = nextFrontier;
            }

            return { outcomeData, totalRows };
        },
        { enabled }
    );

    const reloadOutcomeTree = () => queryCache.invalidateQueries(queryKey);

    return {
        status,
        error,
        outcomeTree: (data?.outcomeData ?? null) as OutcomeTreeData | null | undefined,
        totalRowsPerGame: (data?.totalRows ?? null) as TotalRowsPerGame | null | undefined,
        reloadOutcomeTree,
    };
};

export default useOutcomeTree;
